import React, { useEffect, useState } from "react";
import { Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import ClearCandidatesDialog from "./ClearCandidatesDialog";
import DeleteJobDialog from "./DeleteJobDialog";

// The two destructive actions on a job, from the card.
//
// WHY BEHIND A KEBAB AND NOT ON THE CARD FACE
// The card is a link — the whole surface navigates into the job. Buttons that
// destroy things do not belong on a surface whose main job is to be clicked,
// and a mis-tap on a phone should open a job, never empty one.
//
// Every handler stops propagation for the same reason. Without it, closing the
// menu or cancelling the dialog also navigates, and the confirmation the person
// just declined is replaced by the page they were trying to avoid.
//
// Status is NOT here. It lives on the badge, which was already on the card
// showing the state — putting it in a menu as well would give one setting two
// controls a centimetre apart.

export default function JobCardMenu({ job, onChanged, onDeleted, onOpenChange }) {
  const [anchor, setAnchor] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Tell the card when anything of ours is on screen.
  //
  // stopPropagation on each portal works, but it is a rule every future dialog
  // has to remember — and forgetting it does not look like a missing handler,
  // it looks like "delete sends me to a page that no longer exists". A card
  // that simply refuses to navigate while a menu or dialog is up cannot be
  // broken by the next person who adds one.
  useEffect(() => {
    onOpenChange?.(Boolean(anchor) || clearOpen || deleteOpen);
  }, [anchor, clearOpen, deleteOpen, onOpenChange]);

  const count = Number(job?.candidate_count || 0);

  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      <Tooltip title="More">
        <IconButton
          size="small"
          onClick={(e) => {
            stop(e);
            setAnchor(e.currentTarget);
          }}
          sx={{ color: "#9ca3af", "&:hover": { color: "#4b5563" } }}
        >
          <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={(e) => {
          if (e) stop(e);
          setAnchor(null);
        }}
        onClick={stop}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 214, mt: 0.5 } }}
      >
        <MenuItem
          disabled={count === 0}
          onClick={(e) => {
            stop(e);
            setAnchor(null);
            setClearOpen(true);
          }}
          sx={{ py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <PersonRemoveRoundedIcon sx={{ fontSize: 18, color: "#b45309" }} />
          </ListItemIcon>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            {/* Says the number, so nobody has to open the job to find out how
                much they are about to throw away. */}
            {count === 0 ? "No candidates to remove" : `Remove all ${count} candidates`}
          </Typography>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={(e) => {
            stop(e);
            setAnchor(null);
            setDeleteOpen(true);
          }}
          sx={{ py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeleteOutlineRoundedIcon sx={{ fontSize: 18, color: "#dc2626" }} />
          </ListItemIcon>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#b91c1c" }}>
            Delete job
          </Typography>
        </MenuItem>
      </Menu>

      <ClearCandidatesDialog
        open={clearOpen}
        jobId={job.id}
        jobTitle={job.title}
        count={count}
        onClose={() => setClearOpen(false)}
        onDone={() => {
          setClearOpen(false);
          onChanged?.();
        }}
      />

      <DeleteJobDialog
        open={deleteOpen}
        jobId={job.id}
        jobTitle={job.title}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          // The card this menu lives on is about to disappear, so the list has
          // to refetch rather than the card refreshing itself.
          (onDeleted || onChanged)?.();
        }}
      />
    </>
  );
}
